import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { CommonFunctionsService, DatastoreService } from '../index';

export type OurCeremonyToggle =
	| 'can_reschedule'
	| 'can_extend'
	| 'can_purchase_addons'
	| 'can_cancel';

/**
 * Feature toggles live in the company config at `myCeremony['our-ceremony']`.
 * Fail-closed: a feature is off unless its toggle is explicitly true, so a
 * missing key, missing config, or parse error hides the feature.
 */
export function isOurCeremonyFeatureEnabled(
	dataStore: DatastoreService,
	toggle: OurCeremonyToggle
): boolean {
	try {
		const config =
			typeof dataStore.config === 'string'
				? JSON.parse(dataStore.config)
				: dataStore.config ?? {};
		return config?.myCeremony?.['our-ceremony']?.[toggle] === true;
	} catch (err) {
		console.error('Failed to read our-ceremony feature toggles from config', err);
		return false;
	}
}

/**
 * Blocks a journey's routes when its our-ceremony toggle is off, so hidden
 * buttons can't be bypassed via URL bar / bookmark / back. Reads the same
 * toggle as the buttons, keeping the two in lock-step.
 */
export function ourCeremonyToggleGuard(
	toggle: OurCeremonyToggle
): CanActivateFn {
	return () => {
		const router = inject(Router);
		const dataStore = inject(DatastoreService);
		const commonFunctionsService = inject(CommonFunctionsService);

		if (isOurCeremonyFeatureEnabled(dataStore, toggle)) {
			return true;
		}

		router.navigate([
			`${commonFunctionsService.getCompanyName()}/${commonFunctionsService.getCompanyId()}/our-ceremony`,
		]);
		return false;
	};
}
